import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, Clock, ArrowLeft, ExternalLink, 
  Timer, Users, Palette, MapPin, Ticket,
  Lightbulb, Shirt, Music, Building2, FileText, Image, Film, Radio, ChevronDown, ChevronUp, AlertTriangle, X, ShoppingCart, Lock, UserPlus, Globe, Heart, Share2, History, Search, Map
} from 'lucide-react';
import { ExportCalendarButton } from '../components/events/ExportCalendarButton';
import { useSaleCountdown } from '../components/events/SaleCountdown';
import { CinemaListItem } from '../components/CinemaListItem';
import { CinemaMap } from '../components/CinemaMap';
import { SEOHead, generateEventStructuredData, generateBreadcrumbStructuredData } from '../components/SEOHead';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if user is logged in
const isUserLoggedIn = () => {
  const token = localStorage.getItem('memberToken');
  return !!token;
};

// Get member token
const getMemberToken = () => {
  return localStorage.getItem('memberToken');
};

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

// Comunidades Autónomas de España - mapeo de ciudades
const COMUNIDADES_AUTONOMAS = {
  'Andalucía': ['Sevilla', 'Málaga', 'Granada', 'Córdoba', 'Cádiz', 'Almería', 'Huelva', 'Jaén'],
  'Aragón': ['Zaragoza', 'Huesca', 'Teruel'],
  'Asturias': ['Oviedo', 'Gijón', 'Avilés'],
  'Islas Baleares': ['Palma de Mallorca', 'Ibiza', 'Mahón', 'Mallorca'],
  'Canarias': ['Las Palmas', 'Santa Cruz de Tenerife', 'Tenerife', 'Gran Canaria'],
  'Cantabria': ['Santander', 'Torrelavega'],
  'Castilla-La Mancha': ['Toledo', 'Albacete', 'Ciudad Real', 'Cuenca', 'Guadalajara'],
  'Castilla y León': ['Valladolid', 'Burgos', 'Salamanca', 'León', 'Segovia', 'Ávila', 'Zamora', 'Palencia', 'Soria'],
  'Cataluña': ['Barcelona', 'Tarragona', 'Lleida', 'Girona', 'Lérida', 'Gerona'],
  'Comunidad Valenciana': ['Valencia', 'Alicante', 'Castellón', 'Castellón de la Plana'],
  'Extremadura': ['Badajoz', 'Cáceres', 'Mérida'],
  'Galicia': ['A Coruña', 'Vigo', 'Santiago de Compostela', 'Ourense', 'Pontevedra', 'Lugo', 'La Coruña', 'Orense'],
  'Madrid': ['Madrid', 'Alcalá de Henares', 'Getafe', 'Móstoles', 'Leganés', 'Alcorcón', 'Fuenlabrada', 'Torrejón de Ardoz', 'Alcobendas', 'Las Rozas', 'Pozuelo', 'Majadahonda'],
  'Murcia': ['Murcia', 'Cartagena', 'Lorca'],
  'Navarra': ['Pamplona', 'Tudela'],
  'País Vasco': ['Bilbao', 'San Sebastián', 'Vitoria', 'Donostia', 'Gasteiz'],
  'La Rioja': ['Logroño']
};

// Helper to get comunidad from city
const getComunidadFromCity = (city) => {
  if (!city) return 'Otras';
  const cityNormalized = city.trim();
  
  for (const [comunidad, cities] of Object.entries(COMUNIDADES_AUTONOMAS)) {
    if (cities.some(c => cityNormalized.toLowerCase().includes(c.toLowerCase()) || c.toLowerCase().includes(cityNormalized.toLowerCase()))) {
      return comunidad;
    }
  }
  return 'Otras';
};

// Helper to group cinemas by comunidad
const groupCinemasByComunidad = (cinemas) => {
  const groups = {};
  
  cinemas.forEach((cinema, originalIndex) => {
    const comunidad = getComunidadFromCity(cinema.city);
    if (!groups[comunidad]) {
      groups[comunidad] = [];
    }
    groups[comunidad].push({ ...cinema, originalIndex });
  });
  
  // Sort comunidades alphabetically, but put "Madrid" first if exists
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    if (a === 'Madrid') return -1;
    if (b === 'Madrid') return 1;
    if (a === 'Otras') return 1;
    if (b === 'Otras') return -1;
    return a.localeCompare(b, 'es');
  });
  
  return { groups, sortedKeys };
};

// Helper to extract iframe src from embed_code
const extractIframeSrc = (embedCode) => {
  if (!embedCode) return null;
  const srcMatch = embedCode.match(/src\s*=\s*["']([^"']+)["']/i);
  return srcMatch ? srcMatch[1] : null;
};

// Purchase Modal with iframe - Direct iframe without Eventim script
const PurchaseModal = ({ isOpen, onClose, cinema, eventTitle }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const iframeSrc = extractIframeSrc(cinema.embed_code);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
      <div className="relative w-full max-w-4xl bg-white border border-white/10 flex flex-col" style={{ height: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0 bg-[var(--surface)]">
          <div>
            <h3 className="text-white font-medium">{eventTitle}</h3>
            <p className="text-white/50 text-sm">{cinema.cinema_name} - {cinema.city}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-white/50 hover:text-white p-2"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Iframe Content */}
        <div className="flex-1 overflow-hidden bg-white">
          {iframeSrc ? (
            <iframe
              src={iframeSrc}
              title={`Comprar entradas - ${eventTitle}`}
              className="w-full h-full border-0"
              allow="payment"
              style={{ minHeight: '100%' }}
            />
          ) : cinema.ticket_url ? (
            <div className="flex items-center justify-center h-full bg-[var(--surface)]">
              <div className="text-center py-12">
                <ShoppingCart size={48} className="text-white/20 mx-auto mb-4" />
                <p className="text-white/50 mb-4">Haz clic para ir a la página de compra</p>
                <a 
                  href={cinema.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Ticket size={16} />
                  Comprar entradas
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-[var(--surface)]">
              <p className="text-white/50">No hay enlace de compra configurado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Cinema Card for NON-REGISTERED users - Shows website link and SMART teaser
const CinemaCardPublic = ({ cinema, eventDate, eventTime, isLive, index, hasEventSmart }) => {
  const navigate = useNavigate();
  const { timeLeft, isClosed } = useSaleCountdown(eventDate, eventTime);
  
  // Only show SMART preview if event has SMART AND cinema has SMART
  const showSmartPreview = hasEventSmart && cinema.has_smart && !isClosed;
  
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('es-ES', options);
  };

  return (
    <div className="bg-[var(--surface)] border-2 border-white/10 overflow-hidden" data-testid={`cinema-public-${index}`}>
      {/* Cinema Header */}
      <div className="p-5 border-b border-white/5">
        <p className="text-white font-medium text-lg">{cinema.cinema_name}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/60 mt-2">
          <span className="flex items-center gap-1">
            <MapPin size={14} className="text-[var(--primary)]" /> {cinema.city}
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={14} className="text-[var(--primary)]" /> {formatEventDate(eventDate)}
          </span>
          {(cinema.schedule || (isLive && eventTime)) && (
            <span className="flex items-center gap-1">
              <Clock size={14} className="text-[var(--primary)]" /> 
              {cinema.schedule || eventTime}
            </span>
          )}
        </div>
      </div>

      {/* SMART Preview - ONLY show when event has SMART, cinema has SMART, and sale is NOT closed */}
      {showSmartPreview && (
        <div className="relative">
          <div className="p-4 bg-[var(--primary)]/10 blur-[2px] opacity-60">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Timer size={16} className="text-[var(--primary)]" />
                <span className="text-xs uppercase tracking-wider text-[var(--primary)]">VENTA SMART</span>
              </div>
              <div className="flex gap-3 justify-center">
                {timeLeft && (
                  <>
                    <div className="text-center">
                      <div className="text-2xl font-bold font-mono text-white">{String(timeLeft.hours).padStart(2, '0')}</div>
                      <div className="text-[9px] uppercase text-white/50">horas</div>
                    </div>
                    <span className="text-2xl text-white/30">:</span>
                    <div className="text-center">
                      <div className="text-2xl font-bold font-mono text-white">{String(timeLeft.minutes).padStart(2, '0')}</div>
                      <div className="text-[9px] uppercase text-white/50">min</div>
                    </div>
                  </>
                )}
              </div>
              <p className="text-[var(--primary)] font-bold text-lg mt-2">25% DESCUENTO</p>
            </div>
          </div>
          {/* Overlay CTA */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center p-4">
              <Lock size={24} className="text-[var(--primary)] mx-auto mb-2" />
              <p className="text-white font-medium text-sm mb-2">Venta SMART exclusiva</p>
              <button
                onClick={() => navigate('/mi-cuenta')}
                className="btn-primary text-xs px-4 py-2 flex items-center gap-2 mx-auto"
              >
                <UserPlus size={14} />
                Regístrate gratis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buy button for non-registered users - Secondary style for SMART cinemas */}
      {(cinema.website_url || cinema.ticket_url) && (
        <div className="p-4">
          <a
            href={cinema.website_url || cinema.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 bg-white/10 text-white/80 font-medium hover:bg-white/20 transition-colors flex items-center justify-center gap-2 border border-white/20"
            data-testid={`cinema-buy-${index}`}
          >
            <Globe size={18} />
            Comprar en web del cine
            <ExternalLink size={14} />
          </a>
        </div>
      )}
    </div>
  );
};

// Cinema Card for REGISTERED users - Full SMART access
const CinemaCard = ({ cinema, eventDate, eventTime, isLive, index, eventTitle, onPurchase, hasEventSmart }) => {
  const { timeLeft, isClosed } = useSaleCountdown(eventDate, eventTime);
  const isUrgent = timeLeft && timeLeft.days === 0 && timeLeft.hours < 12;
  
  // Only show SMART if event has SMART AND cinema has SMART
  const showSmart = hasEventSmart && cinema.has_smart && !isClosed;

  // Format event date for display
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('es-ES', options);
  };

  return (
    <div 
      className={`bg-[var(--surface)] border-2 ${showSmart ? (isUrgent ? 'border-red-500/50' : 'border-[var(--primary)]/30') : 'border-white/10'} overflow-hidden`} 
      data-testid={`cinema-${index}`}
    >
      {/* Cinema Header */}
      <div className="p-5 border-b border-white/5">
        <p className="text-white font-medium text-lg">{cinema.cinema_name}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/60 mt-2">
          <span className="flex items-center gap-1">
            <MapPin size={14} className="text-[var(--primary)]" /> {cinema.city}
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={14} className="text-[var(--primary)]" /> {formatEventDate(eventDate)}
          </span>
          {(cinema.schedule || (isLive && eventTime)) && (
            <span className="flex items-center gap-1">
              <Clock size={14} className="text-[var(--primary)]" /> 
              {cinema.schedule || eventTime}
            </span>
          )}
        </div>
      </div>

      {/* SMART Countdown - Only show when event AND cinema have SMART enabled and NOT closed */}
      {showSmart && timeLeft && (
        <div className={`p-4 ${isUrgent ? 'bg-red-950/30' : 'bg-[var(--primary)]/15'}`}>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Timer size={18} className={isUrgent ? 'text-red-400 animate-pulse' : 'text-[var(--primary)]'} />
              <span className={`text-xs uppercase tracking-wider font-bold ${isUrgent ? 'text-red-400' : 'text-[var(--primary)]'}`}>
                VENTA SMART - 25% DESCUENTO
              </span>
            </div>
            <div className="flex gap-3 justify-center">
              {timeLeft.days > 0 && (
                <>
                  <div className="text-center">
                    <div className={`text-3xl font-bold font-mono ${isUrgent ? 'text-red-400' : 'text-white'}`}>
                      {String(timeLeft.days).padStart(2, '0')}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">días</div>
                  </div>
                  <span className={`text-3xl ${isUrgent ? 'text-red-400' : 'text-white/30'}`}>:</span>
                </>
              )}
              <div className="text-center">
                <div className={`text-3xl font-bold font-mono ${isUrgent ? 'text-red-400' : 'text-white'}`}>
                  {String(timeLeft.hours).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">horas</div>
              </div>
              <span className={`text-3xl ${isUrgent ? 'text-red-400' : 'text-white/30'}`}>:</span>
              <div className="text-center">
                <div className={`text-3xl font-bold font-mono ${isUrgent ? 'text-red-400' : 'text-white'}`}>
                  {String(timeLeft.minutes).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">min</div>
              </div>
              <span className={`text-3xl ${isUrgent ? 'text-red-400' : 'text-white/30'}`}>:</span>
              <div className="text-center">
                <div className={`text-3xl font-bold font-mono ${isUrgent ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                  {String(timeLeft.seconds).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">seg</div>
              </div>
            </div>
            <p className="text-white/40 text-[10px] mt-2">La venta SMART se cierra 48h antes del evento</p>
          </div>
        </div>
      )}

      {/* Buttons - Two options for registered users when SMART is available */}
      <div className="p-4 space-y-3">
        {/* SMART Button - Prominent gold design */}
        {showSmart && cinema.embed_code && (
          <button
            onClick={() => onPurchase(cinema)}
            className="w-full text-base flex items-center justify-center gap-2 py-4 font-bold bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black hover:from-[#E5C04B] hover:to-[#F5E050] transition-all shadow-lg shadow-[#D4AF37]/20"
            data-testid={`cinema-smart-${index}`}
          >
            <Timer size={18} />
            <span>Comprar con 25% DESCUENTO</span>
          </button>
        )}

        {/* Regular Buy Button - Subtle secondary style */}
        {(cinema.website_url || cinema.ticket_url) && (
          <a
            href={cinema.website_url || cinema.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-sm flex items-center justify-center gap-2 py-3 transition-colors bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border border-white/10"
            data-testid={`cinema-buy-${index}`}
          >
            <Globe size={16} />
            Comprar en web del cine
            <ExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  );
};

// Cinemas Section with SMART/Normal separation - Organized by Comunidad Autónoma
const CinemasSection = ({ cinemas, eventDate, eventTime, isLive, eventTitle, onPurchase, hasEventSmart, onOpenMap }) => {
  const isLoggedIn = isUserLoggedIn();
  const navigate = useNavigate();
  
  // Search filter state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter cinemas by search term (city or cinema name)
  const filterCinemas = (list) => {
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase().trim();
    return list.filter(cinema => 
      cinema.cinema_name?.toLowerCase().includes(term) ||
      cinema.city?.toLowerCase().includes(term) ||
      cinema.comunidad_autonoma?.toLowerCase().includes(term)
    );
  };
  
  // Separate SMART cinemas from regular ones
  const allSmartCinemas = hasEventSmart ? cinemas.filter(c => c.has_smart && c.embed_code).map((c, i) => ({ ...c, originalIndex: cinemas.indexOf(c) })) : [];
  const allRegularCinemas = cinemas.filter(c => !c.has_smart || !c.embed_code).map((c, i) => ({ ...c, originalIndex: cinemas.indexOf(c) }));
  
  // Apply search filter
  const smartCinemas = filterCinemas(allSmartCinemas);
  const regularCinemas = filterCinemas(allRegularCinemas);
  
  // Group by Comunidad Autónoma for each section
  const groupByComunidad = (list) => {
    const groups = {};
    list.forEach((cinema) => {
      const comunidad = cinema.comunidad_autonoma || 'Otras';
      if (!groups[comunidad]) groups[comunidad] = [];
      groups[comunidad].push(cinema);
    });
    
    // Sort cinemas within each group: first by city, then by cinema name
    Object.keys(groups).forEach(comunidad => {
      groups[comunidad].sort((a, b) => {
        // First compare by city
        const cityA = (a.city || '').toLowerCase();
        const cityB = (b.city || '').toLowerCase();
        if (cityA !== cityB) {
          return cityA.localeCompare(cityB, 'es');
        }
        // Then compare by cinema name
        const nameA = (a.cinema_name || '').toLowerCase();
        const nameB = (b.cinema_name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'es');
      });
    });
    
    const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
    return { groups, sortedKeys };
  };
  
  const smartGroups = groupByComunidad(smartCinemas);
  const regularGroups = groupByComunidad(regularCinemas);
  
  // Collapse all comunidades by default, or expand when searching
  const [expandedSmart, setExpandedSmart] = useState([]);
  const [expandedRegular, setExpandedRegular] = useState([]);

  // Handle search term changes - expand all when searching
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value.trim()) {
      // Auto-expand all when user is searching
      setExpandedSmart(smartGroups.sortedKeys);
      setExpandedRegular(regularGroups.sortedKeys);
    }
  };

  const toggleComunidad = (comunidad, isSmart) => {
    if (isSmart) {
      setExpandedSmart(prev => prev.includes(comunidad) ? prev.filter(c => c !== comunidad) : [...prev, comunidad]);
    } else {
      setExpandedRegular(prev => prev.includes(comunidad) ? prev.filter(c => c !== comunidad) : [...prev, comunidad]);
    }
  };

  const expandAllSmart = () => setExpandedSmart(smartGroups.sortedKeys);
  const collapseAllSmart = () => setExpandedSmart([]);
  const expandAllRegular = () => setExpandedRegular(regularGroups.sortedKeys);
  const collapseAllRegular = () => setExpandedRegular([]);

  return (
    <div className="space-y-8">
      {/* SMART Banner for non-logged users */}
      {!isLoggedIn && smartCinemas.length > 0 && (
        <div className="p-6 bg-gradient-to-r from-[var(--primary)]/20 to-transparent border-2 border-[var(--primary)]/40">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[var(--primary)]/20 flex items-center justify-center">
                <Ticket size={28} className="text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="text-white font-medium text-lg">¿Quieres un 25% de descuento?</h3>
                <p className="text-white/60 text-sm">Regístrate gratis en myEventCinema y accede a la venta SMART exclusiva</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/mi-cuenta')}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              <UserPlus size={18} />
              Regístrate gratis
            </button>
          </div>
        </div>
      )}

      {/* ==================== SEARCH BAR + MAP BUTTON ==================== */}
      <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center" data-testid="cinema-search">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por ciudad o nombre del cine..."
            className="w-full bg-white/5 border border-white/10 text-white placeholder-white/40 pl-12 pr-4 py-3 focus:outline-none focus:border-[var(--primary)]/50 transition-colors"
            data-testid="cinema-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>
        
        {/* Ver Mapa Button */}
        <button
          onClick={onOpenMap}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-[var(--primary)]/10 border border-[var(--primary)]/40 text-[var(--primary)] hover:bg-[var(--primary)]/20 transition-colors whitespace-nowrap"
          data-testid="view-map-btn"
        >
          <Map size={18} />
          <span className="font-medium">Ver Mapa</span>
          <span className="text-white/50 text-sm">({cinemas.length})</span>
        </button>
      </div>
      
      {searchTerm && (
        <div className="text-sm text-white/50 -mt-2">
          {smartCinemas.length + regularCinemas.length === 0 ? (
            <span className="text-red-400">No se encontraron cines que coincidan con &quot;{searchTerm}&quot;</span>
          ) : (
            <span>
              Mostrando {smartCinemas.length + regularCinemas.length} cine{smartCinemas.length + regularCinemas.length !== 1 ? 's' : ''} 
              {smartCinemas.length > 0 && ` (${smartCinemas.length} SMART)`}
            </span>
          )}
        </div>
      )}

      {/* ==================== SMART CINEMAS SECTION ==================== */}
      {smartCinemas.length > 0 && (
        <div className="bg-gradient-to-br from-[var(--primary)]/10 to-transparent border-2 border-[var(--primary)]/30 p-6" data-testid="smart-cinemas-section">
          {/* SMART Section Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-[var(--primary)]/20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[var(--primary)] flex items-center justify-center">
                <Timer size={24} className="text-black" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="font-['Playfair_Display'] text-2xl text-white">Cines con Venta SMART</h2>
                  <span className="bg-[var(--primary)] text-black text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    25% DESCUENTO
                  </span>
                </div>
                <p className="text-[var(--primary)]/80 text-sm mt-1">
                  ¡Aprovecha el descuento exclusivo para socios myEventCinema!
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <button onClick={expandAllSmart} className="text-xs text-[var(--primary)]/60 hover:text-[var(--primary)] transition-colors">
                  Expandir
                </button>
                <span className="text-[var(--primary)]/30">|</span>
                <button onClick={collapseAllSmart} className="text-xs text-[var(--primary)]/60 hover:text-[var(--primary)] transition-colors">
                  Colapsar
                </button>
              </div>
              <span className="text-[var(--primary)] font-bold text-2xl">{smartCinemas.length}</span>
            </div>
          </div>

          {/* SMART Cinemas by Comunidad Autónoma */}
          <div className="space-y-3">
            {smartGroups.sortedKeys.map(comunidad => {
              const comunidadCinemas = smartGroups.groups[comunidad];
              const isExpanded = expandedSmart.includes(comunidad);
              
              return (
                <div key={comunidad} className="border border-[var(--primary)]/20 bg-black/20">
                  <button
                    onClick={() => toggleComunidad(comunidad, true)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--primary)]/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin size={18} className="text-[var(--primary)]" />
                      <span className="text-white font-medium">{comunidad}</span>
                      <span className="text-[var(--primary)]/60 text-sm">({comunidadCinemas.length} {comunidadCinemas.length === 1 ? 'cine' : 'cines'})</span>
                    </div>
                    <ChevronDown size={20} className={`text-[var(--primary)] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="p-4 pt-0 border-t border-[var(--primary)]/20">
                      <div className="flex flex-col gap-3 mt-4">
                        {comunidadCinemas.map((cinema) => (
                          <CinemaListItem
                            key={cinema.originalIndex}
                            cinema={cinema}
                            eventDate={eventDate}
                            eventTime={eventTime}
                            isLive={isLive}
                            index={cinema.originalIndex}
                            isSmart={true}
                            onPurchase={onPurchase}
                            isLoggedIn={isLoggedIn}
                            showCountdown={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==================== REGULAR CINEMAS SECTION ==================== */}
      {regularCinemas.length > 0 && (
        <div className="bg-white/5 border border-white/10 p-6" data-testid="regular-cinemas-section">
          {/* Regular Section Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center">
                <Building2 size={24} className="text-white/70" />
              </div>
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl text-white/80">Otros Cines</h2>
                <p className="text-white/40 text-sm mt-1">
                  Compra directamente en la web del cine
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <button onClick={expandAllRegular} className="text-xs text-white/40 hover:text-white/70 transition-colors">
                  Expandir
                </button>
                <span className="text-white/20">|</span>
                <button onClick={collapseAllRegular} className="text-xs text-white/40 hover:text-white/70 transition-colors">
                  Colapsar
                </button>
              </div>
              <span className="text-white/50 font-bold text-2xl">{regularCinemas.length}</span>
            </div>
          </div>

          {/* Regular Cinemas by Comunidad Autónoma */}
          <div className="space-y-3">
            {regularGroups.sortedKeys.map(comunidad => {
              const comunidadCinemas = regularGroups.groups[comunidad];
              const isExpanded = expandedRegular.includes(comunidad);
              
              return (
                <div key={comunidad} className="border border-white/10 bg-black/20">
                  <button
                    onClick={() => toggleComunidad(comunidad, false)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin size={18} className="text-white/50" />
                      <span className="text-white/80 font-medium">{comunidad}</span>
                      <span className="text-white/40 text-sm">({comunidadCinemas.length} {comunidadCinemas.length === 1 ? 'cine' : 'cines'})</span>
                    </div>
                    <ChevronDown size={20} className={`text-white/50 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="p-4 pt-0 border-t border-white/10">
                      <div className="flex flex-col gap-3 mt-4">
                        {comunidadCinemas.map((cinema) => (
                          <CinemaListItem
                            key={cinema.originalIndex}
                            cinema={cinema}
                            eventDate={eventDate}
                            eventTime={eventTime}
                            isLive={isLive}
                            index={cinema.originalIndex}
                            isSmart={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Regular Cinema Card - Simple, no SMART
const RegularCinemaCard = ({ cinema, eventDate, eventTime, isLive, index }) => {
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="bg-[var(--surface)] border border-white/10 overflow-hidden" data-testid={`regular-cinema-${index}`}>
      <div className="p-5 border-b border-white/5">
        <p className="text-white/80 font-medium text-lg">{cinema.cinema_name}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50 mt-2">
          <span className="flex items-center gap-1">
            <MapPin size={14} className="text-white/40" /> {cinema.city}
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={14} className="text-white/40" /> {formatEventDate(eventDate)}
          </span>
          {(cinema.schedule || (isLive && eventTime)) && (
            <span className="flex items-center gap-1">
              <Clock size={14} className="text-white/40" /> 
              {cinema.schedule || eventTime}
            </span>
          )}
        </div>
      </div>

      {/* Only Regular Buy Button */}
      {(cinema.website_url || cinema.ticket_url) && (
        <div className="p-4">
          <a
            href={cinema.website_url || cinema.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-base flex items-center justify-center gap-2 py-3 bg-white/10 text-white/80 font-medium hover:bg-white/20 transition-colors border border-white/20"
            data-testid={`regular-cinema-buy-${index}`}
          >
            <Globe size={18} />
            Comprar en web del cine
            <ExternalLink size={14} />
          </a>
        </div>
      )}
    </div>
  );
};

export const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null); // Now stores index instead of URL
  const [showFullCrew, setShowFullCrew] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState({ isOpen: false, cinema: null });
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);

  // Meta Pixel tracking - only active if event has pixel configured and cookies accepted
  const { trackViewContent, trackInitiateCheckout } = useMetaPixel(
    event?.meta_pixel_id,
    event?.meta_pixel_enabled
  );

  useEffect(() => {
    fetchEvent();
  }, [id]);

  // Track ViewContent when event loads (for Meta Pixel)
  useEffect(() => {
    if (event && event.meta_pixel_enabled && event.meta_pixel_id) {
      trackViewContent({
        content_id: event.id,
        content_name: event.title,
        content_category: event.genre,
        value: 0, // Could be ticket price if available
        currency: 'EUR'
      });
    }
  }, [event, trackViewContent]);

  useEffect(() => {
    // Check if event is in favorites when user is logged in
    if (event && isUserLoggedIn()) {
      checkFavoriteStatus();
    }
  }, [event]);

  const checkFavoriteStatus = async () => {
    const token = getMemberToken();
    if (!token) return;
    try {
      const response = await axios.get(`${API}/members/favorites/events?token=${token}`);
      const favoriteIds = response.data.map(e => e.id);
      setIsFavorite(favoriteIds.includes(id));
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isUserLoggedIn()) {
      toast.info('Regístrate para guardar favoritos');
      navigate('/mi-cuenta');
      return;
    }
    
    const token = getMemberToken();
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/members/favorites/events/${id}?token=${token}`);
        setIsFavorite(false);
        toast.success('Eliminado de favoritos');
      } else {
        await axios.post(`${API}/members/favorites/events/${id}?token=${token}`);
        setIsFavorite(true);
        toast.success('¡Añadido a favoritos! +5 puntos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: event.title,
      text: `Mira este evento: ${event.title}`,
      url: shareUrl
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        // Award points for sharing if logged in
        if (isUserLoggedIn()) {
          try {
            const token = getMemberToken();
            await axios.post(`${API}/members/points/share?token=${token}&event_id=${id}`);
            toast.success('¡Compartido! +5 puntos');
          } catch (e) {
            // Points might already be awarded for this event
          }
        }
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('¡Enlace copiado!');
      }
    } catch (error) {
      // User cancelled share
    }
  };

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
      const relatedResponse = await axios.get(`${API}/events?genre=${response.data.genre}`);
      const now = new Date().toISOString().split('T')[0];
      setRelatedEvents(
        relatedResponse.data
          .filter(e => e.id !== id)
          .filter(e => {
            if (e.show_on_home === false) return false;
            if (e.event_type === 'directo' && e.date && e.date < now) return false;
            return true;
          })
          .slice(0, 3)
      );
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (cinema) => {
    // Track InitiateCheckout for Meta Pixel
    if (event?.meta_pixel_enabled && event?.meta_pixel_id) {
      trackInitiateCheckout({
        content_ids: [event.id],
        content_name: event.title,
        content_category: event.genre,
        value: 0,
        currency: 'EUR',
        num_items: 1
      });
    }
    
    if (cinema.embed_code) {
      setPurchaseModal({ isOpen: true, cinema });
    } else {
      window.open(cinema.ticket_url, '_blank');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const genreLabels = {
    opera: 'Ópera',
    ballet: 'Ballet',
    concierto: 'Concierto',
    teatro: 'Teatro',
    musical: 'Musical',
    cine: 'Cine'
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24" data-testid="event-detail-loading">
        <div className="h-[70vh] skeleton" />
        <div className="section-container py-12">
          <div className="h-8 skeleton w-1/2 mb-4" />
          <div className="h-4 skeleton w-3/4" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="event-not-found">
        <div className="text-center">
          <h1 className="text-4xl text-white mb-4">Evento no encontrado</h1>
          <Link to="/catalogo" className="btn-primary">Volver al catálogo</Link>
        </div>
      </div>
    );
  }

  const crew = event.crew || {};
  const cinemas = event.cinemas || [];
  const gallery = event.gallery || [];
  const youtubeId = getYouTubeId(event.trailer_url);
  const isLive = event.event_type === 'directo';
  const hasCrew = Object.keys(crew).length > 0;
  
  // Check if event date is in the past
  const isPastEvent = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(event.date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate < today;
  };
  
  const eventIsPast = isPastEvent();

  // Generate SEO structured data for the event
  const eventStructuredData = generateEventStructuredData(event, cinemas);
  const breadcrumbData = generateBreadcrumbStructuredData([
    { name: 'Inicio', url: '/' },
    { name: 'Catálogo', url: '/catalogo' },
    { name: event.title }
  ]);

  return (
    <div className="min-h-screen" data-testid="event-detail-page">
      <SEOHead 
        title={event.title}
        description={`${event.title} - ${isLive ? 'Retransmisión en directo' : 'Grabación'} de ${genreLabels[event.genre] || event.genre} desde ${event.venue}. Compra tus entradas para verlo en cines de España${event.has_smart ? ' con hasta 25% de descuento' : ''}.`}
        keywords={`${event.title}, ${event.venue}, ${genreLabels[event.genre] || event.genre} en cine, entradas ${event.title}, ${isLive ? 'directo' : 'grabado'}, cines España`}
        canonicalUrl={`/evento/${id}`}
        ogImage={event.poster_url || event.image_url}
        ogType="event"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [eventStructuredData, breadcrumbData]
        }}
      />
      
      {/* Hero */}
      <section className="detail-hero relative">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${event.image_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/70 to-transparent" />
        
        <div className="relative z-10 section-container pt-32 pb-20 min-h-[70vh] flex flex-col justify-end">
          <Link to="/catalogo" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8" data-testid="back-to-catalog">
            <ArrowLeft size={18} />
            <span className="text-xs uppercase tracking-widest">Volver al catálogo</span>
          </Link>

          <div className="flex gap-3 mb-6">
            <span className={isLive ? 'badge-live' : 'badge-recorded'}>
              {isLive ? 'En Directo' : 'Grabado'}
            </span>
            <span className="bg-[var(--secondary)] text-white text-xs px-3 py-1 uppercase tracking-wider font-medium">
              {genreLabels[event.genre] || event.genre}
            </span>
          </div>

          <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl text-white mb-6 max-w-4xl">{event.title}</h1>

          <div className="flex flex-wrap gap-6 text-white/70">
            <span className="flex items-center gap-2">
              <Calendar size={18} className="text-[var(--primary)]" />
              {event.event_type === 'directo' ? formatDate(event.date) : `Estreno en cines ${formatDate(event.date)}`}
            </span>
            {event.event_type === 'directo' && (
              <span className="flex items-center gap-2"><Clock size={18} className="text-[var(--primary)]" />{event.time}</span>
            )}
            <span className="flex items-center gap-2"><Timer size={18} className="text-[var(--primary)]" />{formatDuration(event.duration_minutes)}</span>
            <span className="flex items-center gap-2"><Building2 size={18} className="text-[var(--primary)]" />{event.venue}</span>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-4 mt-8">
            <ExportCalendarButton eventId={event.id} eventTitle={event.title} genre={event.genre} city={event.venue} />
            
            {/* Favorite Button */}
            <button
              onClick={toggleFavorite}
              disabled={favoriteLoading}
              className={`inline-flex items-center gap-2 px-5 py-2.5 border transition-all duration-300 ${
                isFavorite 
                  ? 'bg-[var(--primary)] border-[var(--primary)] text-black' 
                  : 'border-white/30 text-white hover:border-[var(--primary)] hover:text-[var(--primary)]'
              }`}
              data-testid="favorite-btn"
            >
              <Heart size={18} className={isFavorite ? 'fill-current' : ''} />
              <span className="text-sm uppercase tracking-wider">
                {favoriteLoading ? '...' : (isFavorite ? 'Favorito' : 'Añadir a favoritos')}
              </span>
            </button>
            
            {/* Share Button */}
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/30 text-white hover:border-[var(--primary)] hover:text-[var(--primary)] transition-all duration-300"
              data-testid="share-btn"
            >
              <Share2 size={18} />
              <span className="text-sm uppercase tracking-wider">Compartir</span>
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-container py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column: Description + Cinemas */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl text-white mb-6">Sobre la producción</h2>
              <p className="text-white/70 leading-relaxed text-lg whitespace-pre-line">{event.description}</p>
            </div>

            {/* Past Event Notice */}
            {eventIsPast && (
              <div className="p-6 bg-white/5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar size={24} className="text-white/40" />
                  <h3 className="text-xl text-white/70 font-medium">Evento Pasado</h3>
                </div>
                <p className="text-white/50">
                  Este evento ya se ha celebrado. Puedes explorar otros eventos disponibles en nuestro catálogo.
                </p>
                <Link to="/catalogo" className="btn-outline inline-flex items-center gap-2 mt-4">
                  Ver próximos eventos
                  <ArrowLeft size={16} className="rotate-180" />
                </Link>
              </div>
            )}

            {/* Cinemas Section with Countdown - GROUPED BY COMUNIDAD - Only show for future events */}
            {cinemas.length > 0 && !eventIsPast && (
              <CinemasSection 
                cinemas={cinemas}
                eventDate={event.date}
                eventTime={event.time}
                isLive={isLive}
                eventTitle={event.title}
                onPurchase={handlePurchase}
                hasEventSmart={event.has_smart_sale}
                onOpenMap={() => setShowMap(true)}
              />
            )}
            
            {/* Cinema Map Modal */}
            <CinemaMap
              cinemas={cinemas}
              isOpen={showMap}
              onClose={() => setShowMap(false)}
              eventTitle={event.title}
              onPurchase={handlePurchase}
              isLoggedIn={isUserLoggedIn()}
            />
          </div>

          {/* Right Column: Info Panel - REDESIGNED */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 backdrop-blur-xl bg-[#121212]/80 border border-white/10 shadow-2xl relative overflow-hidden">
              {/* Gold Trace Top Border */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent" />
              
              {/* Event Info Header */}
              <div className="p-6 md:p-8">
                <h3 className="font-['Playfair_Display'] text-2xl text-white font-semibold tracking-tight mb-8">
                  Información del evento
                </h3>
                
                {/* Event Type Badge */}
                <div className="mb-8">
                  {isLive ? (
                    <span className="inline-flex items-center gap-2 bg-red-900/30 text-red-400 border border-red-900/50 px-4 py-2 text-xs uppercase tracking-widest font-bold">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      En Directo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 bg-[var(--primary)]/10 text-[var(--primary)] border border-[var(--primary)]/30 px-4 py-2 text-xs uppercase tracking-widest font-medium">
                      <Film size={12} />
                      Grabado
                    </span>
                  )}
                </div>

                {/* Info Items with Circle Icons */}
                <div className="space-y-6">
                  {/* Date & Time Combined */}
                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 text-[var(--primary)] group-hover:bg-[var(--primary)] group-hover:text-black transition-all duration-300">
                      <Calendar size={20} />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">
                        {isLive ? 'Fecha y Hora' : 'Estreno en cines'}
                      </span>
                      <span className="text-lg font-medium text-white block">
                        {formatDate(event.date)}
                      </span>
                      {isLive && event.time && (
                        <span className="text-[var(--primary)] text-lg font-semibold">{event.time}</span>
                      )}
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 text-[var(--primary)] group-hover:bg-[var(--primary)] group-hover:text-black transition-all duration-300">
                      <Timer size={20} />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Duración</span>
                      <span className="text-lg font-medium text-white">{formatDuration(event.duration_minutes)}</span>
                    </div>
                  </div>

                  {/* Venue */}
                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 text-[var(--primary)] group-hover:bg-[var(--primary)] group-hover:text-black transition-all duration-300">
                      <Building2 size={20} />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Teatro</span>
                      <span className="text-base font-medium text-white leading-tight">{event.venue}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {/* Ficha Artística - Collapsible with New Design */}
              {hasCrew && (
                <div className="border-b border-white/5">
                  <button 
                    onClick={() => setShowFullCrew(!showFullCrew)}
                    className="w-full px-6 md:px-8 py-5 flex items-center justify-between text-white hover:bg-white/5 transition-colors group"
                  >
                    <span className="flex items-center gap-3 font-['Playfair_Display'] text-lg font-medium group-hover:text-[var(--primary)] transition-colors">
                      <Users size={20} className="text-[var(--primary)]" />
                      Ficha Artística
                    </span>
                    <ChevronDown size={20} className={`text-[var(--primary)] transition-transform duration-300 ${showFullCrew ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <div className={`overflow-hidden transition-all duration-300 ${showFullCrew ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 md:px-8 pb-6 grid grid-cols-2 gap-6">
                      {crew.author && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Autor</span>
                          <span className="text-base font-medium text-white">{crew.author}</span>
                        </div>
                      )}
                      {crew.director && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Director</span>
                          <span className="text-base font-medium text-white">{crew.director}</span>
                        </div>
                      )}
                      {crew.musical_direction && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Dir. Musical</span>
                          <span className="text-base font-medium text-white">{crew.musical_direction}</span>
                        </div>
                      )}
                      {crew.choreography && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Coreografía</span>
                          <span className="text-base font-medium text-white">{crew.choreography}</span>
                        </div>
                      )}
                      {crew.scenography && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Escenografía</span>
                          <span className="text-base font-medium text-white">{crew.scenography}</span>
                        </div>
                      )}
                      {crew.lighting && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Iluminación</span>
                          <span className="text-base font-medium text-white">{crew.lighting}</span>
                        </div>
                      )}
                      {crew.costume_design && (
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Vestuario</span>
                          <span className="text-base font-medium text-white">{crew.costume_design}</span>
                        </div>
                      )}
                      {crew.orchestra && (
                        <div className="col-span-2">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-1">Orquesta</span>
                          <span className="text-base font-medium text-white">{crew.orchestra}</span>
                        </div>
                      )}
                      {crew.cast && crew.cast.length > 0 && (
                        <div className="col-span-2 pt-2">
                          <span className="text-xs uppercase tracking-widest text-white/50 block mb-3">Elenco</span>
                          <div className="flex flex-wrap gap-2">
                            {crew.cast.map((actor, i) => (
                              <span key={i} className="px-3 py-1.5 bg-white/5 text-white text-sm border border-white/10 hover:border-[var(--primary)]/50 hover:bg-[var(--primary)]/10 transition-all cursor-default">{actor}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* PDF Download - Redesigned Button */}
              {event.pdf_url && (
                <div className="px-6 md:px-8 py-6 border-b border-white/5">
                  <a 
                    href={event.pdf_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full flex items-center justify-center gap-3 border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-black px-6 py-4 uppercase tracking-widest text-xs font-bold transition-all duration-300 group"
                    data-testid="download-pdf-btn"
                  >
                    <FileText size={16} className="group-hover:scale-110 transition-transform" />
                    Descargar Ficha PDF
                    <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              )}

              {/* Trailer Section - Redesigned */}
              {youtubeId && (
                <div className="border-b border-white/5">
                  <button 
                    onClick={() => setShowTrailer(!showTrailer)}
                    className="w-full px-6 md:px-8 py-5 flex items-center justify-between text-white hover:bg-white/5 transition-colors group"
                  >
                    <span className="flex items-center gap-3 font-['Playfair_Display'] text-lg font-medium group-hover:text-[var(--primary)] transition-colors">
                      <Film size={20} className="text-[var(--primary)]" />
                      Ver Tráiler
                    </span>
                    <ChevronDown size={20} className={`text-[var(--primary)] transition-transform duration-300 ${showTrailer ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <div className={`overflow-hidden transition-all duration-300 ${showTrailer ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 md:px-8 pb-6">
                      <div className="relative aspect-video bg-black border border-white/10">
                        <iframe
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          title={`Tráiler de ${event.title}`}
                          className="absolute inset-0 w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Gallery Section - Collapsible with New Design */}
              {gallery.length > 0 && (
                <div>
                  <button 
                    onClick={() => setShowGallery(!showGallery)}
                    className="w-full px-6 md:px-8 py-5 flex items-center justify-between text-white hover:bg-white/5 transition-colors group"
                  >
                    <span className="flex items-center gap-3 font-['Playfair_Display'] text-lg font-medium group-hover:text-[var(--primary)] transition-colors">
                      <Image size={20} className="text-[var(--primary)]" />
                      Galería
                      <span className="text-xs text-white/50 font-normal ml-1">({gallery.length})</span>
                    </span>
                    <ChevronDown size={20} className={`text-[var(--primary)] transition-transform duration-300 ${showGallery ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <div className={`overflow-hidden transition-all duration-300 ${showGallery ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 md:px-8 pb-6 max-h-[400px] overflow-y-auto">
                      <div className="grid grid-cols-2 gap-3">
                        {gallery.map((img, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImage(index)}
                            className="aspect-square overflow-hidden group cursor-pointer border border-white/10 hover:border-[var(--primary)]/50 transition-all"
                            data-testid={`gallery-image-${index}`}
                          >
                            <img 
                              src={img} 
                              alt={`${event.title} - ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Share Section */}
              <div className="px-6 md:px-8 py-6">
                <h4 className="text-xs uppercase tracking-widest text-white/50 mb-4">Compartir evento</h4>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Facebook */}
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-[#1877F2] hover:text-white transition-all duration-300"
                    data-testid="share-facebook"
                    title="Compartir en Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  
                  {/* X (Twitter) */}
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Mira este evento: ${event.title}`)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-black hover:text-white hover:ring-1 hover:ring-white/30 transition-all duration-300"
                    data-testid="share-twitter"
                    title="Compartir en X"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  
                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(window.location.href);
                      toast.success('¡Enlace copiado! Pégalo en Instagram');
                      window.open('https://www.instagram.com/', '_blank');
                    }}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] hover:text-white transition-all duration-300"
                    data-testid="share-instagram"
                    title="Compartir en Instagram"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  
                  {/* WhatsApp */}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Mira este evento: ${event.title} - ${window.location.href}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-[#25D366] hover:text-white transition-all duration-300"
                    data-testid="share-whatsapp"
                    title="Compartir en WhatsApp"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                  
                  {/* Email */}
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`Te recomiendo: ${event.title}`)}&body=${encodeURIComponent(`Mira este evento que he encontrado:\n\n${event.title}\n\n${window.location.href}`)}`}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-[#EA4335] hover:text-white transition-all duration-300"
                    data-testid="share-email"
                    title="Enviar por correo"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </a>
                  
                  {/* Copy Link */}
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(window.location.href);
                      toast.success('¡Enlace copiado!');
                    }}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/60 hover:bg-[var(--primary)] hover:text-black transition-all duration-300"
                    data-testid="share-copy"
                    title="Copiar enlace"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Lightbox with Navigation */}
      {selectedImage !== null && gallery.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 text-white/50 hover:text-white z-10 w-12 h-12 flex items-center justify-center"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          
          {/* Image counter */}
          <div className="absolute top-4 left-4 text-white/50 text-sm">
            {selectedImage + 1} / {gallery.length}
          </div>

          {/* Previous button */}
          {gallery.length > 1 && (
            <button 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-[var(--primary)] text-white hover:text-black transition-all z-10"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(selectedImage === 0 ? gallery.length - 1 : selectedImage - 1);
              }}
            >
              <ChevronDown size={28} className="rotate-90" />
            </button>
          )}

          {/* Image */}
          <img 
            src={gallery[selectedImage]} 
            alt={`Imagen ${selectedImage + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {gallery.length > 1 && (
            <button 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-[var(--primary)] text-white hover:text-black transition-all z-10"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(selectedImage === gallery.length - 1 ? 0 : selectedImage + 1);
              }}
            >
              <ChevronDown size={28} className="-rotate-90" />
            </button>
          )}

          {/* Thumbnail strip */}
          {gallery.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto p-2">
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(idx);
                  }}
                  className={`w-16 h-16 flex-shrink-0 overflow-hidden border-2 transition-all ${
                    idx === selectedImage ? 'border-[var(--primary)]' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Purchase Modal */}
      <PurchaseModal 
        isOpen={purchaseModal.isOpen}
        onClose={() => setPurchaseModal({ isOpen: false, cinema: null })}
        cinema={purchaseModal.cinema || {}}
        eventTitle={event?.title || ''}
      />

      {/* Related Events */}
      {relatedEvents.length > 0 && (
        <section className="section-container section-spacing bg-[var(--surface)]">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">Te puede interesar</span>
              <h2 className="font-['Playfair_Display'] text-3xl text-white">Eventos similares</h2>
            </div>
            <Link to={`/catalogo?genre=${event.genre}`} className="btn-ghost" data-testid="view-more-genre">
              Ver más {genreLabels[event.genre]}
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedEvents.map(relatedEvent => (
              <Link key={relatedEvent.id} to={`/evento/${relatedEvent.id}`} className="card-event group block" data-testid={`related-event-${relatedEvent.id}`}>
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={relatedEvent.image_url} alt={relatedEvent.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl text-white group-hover:text-[var(--primary)] transition-colors">{relatedEvent.title}</h3>
                  <div className="flex items-center gap-4 mt-3 text-sm text-white/50">
                    <span className="flex items-center gap-1"><Calendar size={14} />{new Date(relatedEvent.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                    <span className="flex items-center gap-1"><Building2 size={14} />{relatedEvent.venue}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default EventDetail;
